import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Search, MoreVertical, Plus } from "lucide-react";
import { RecruiterShell } from "@/shell/RecruiterShell";
import { api } from "@/lib/api";
import type { CandidatesData, CandidateRow, ArchivedCandidateRow } from "@/lib/types";

function formatActivity(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

// ── Status badge ─────────────────────────────────────────────────────────────

type CandidateStatus = CandidateRow["status"];

const STATUS_CONFIG: Record<
  CandidateStatus,
  { label: string; dot: string; bg: string; color: string }
> = {
  invited: {
    label: "Invited",
    dot: "var(--accent)",
    bg: "var(--accent-50)",
    color: "#4A4260",
  },
  in_progress: {
    label: "In progress",
    dot: "#B5811F",
    bg: "#F5ECD6",
    color: "#B5811F",
  },
  completed: {
    label: "Completed",
    dot: "#22A06B",
    bg: "#E6F5EE",
    color: "#22A06B",
  },
  expired: {
    label: "Expired",
    dot: "#8C86A0",
    bg: "#EAE4F3",
    color: "#7C7590",
  },
};

function StatusBadge({ status }: { status: CandidateStatus }) {
  const cfg = STATUS_CONFIG[status] ?? {
    label: status,
    dot: "#8C86A0",
    bg: "#EAE4F3",
    color: "#7C7590",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 999,
        background: cfg.bg,
        color: cfg.color,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </span>
  );
}

// ── Avatar initials circle ────────────────────────────────────────────────────

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 34,
        height: 34,
        borderRadius: "50%",
        background: "var(--accent-50)",
        color: "#4A4260",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "0.04em",
        flexShrink: 0,
        userSelect: "none",
      }}
    >
      {initials || "?"}
    </span>
  );
}

// ── Filter tab types ──────────────────────────────────────────────────────────

type FilterKey = "all" | "needs_review" | "invited" | "completed" | "expired";

// ── Row actions menu ──────────────────────────────────────────────────────────

function RowMenu({ email, onArchive }: { email: string; onArchive: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          borderRadius: 6,
          color: "#7C7590",
          padding: 0,
        }}
        aria-label="Candidate actions"
      >
        <MoreVertical size={15} />
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: 32,
            background: "#FFFFFF",
            border: "1px solid #EFEBF7",
            borderRadius: 10,
            boxShadow: "0 6px 20px -8px rgba(45,30,90,.18), 0 1px 2px rgba(45,30,90,.06)",
            minWidth: 156,
            zIndex: 20,
            overflow: "hidden",
          }}
        >
          <a
            href={`/coach/recruiter/candidate/${encodeURIComponent(email)}`}
            style={{
              display: "block",
              padding: "9px 14px",
              fontSize: 13,
              color: "#17131F",
              textDecoration: "none",
              fontWeight: 500,
            }}
            onClick={() => setOpen(false)}
          >
            View profile
          </a>
          <a
            href={`/coach/recruiter/invite/new?email=${encodeURIComponent(email)}`}
            style={{
              display: "block",
              padding: "9px 14px",
              fontSize: 13,
              color: "#17131F",
              textDecoration: "none",
              fontWeight: 500,
              borderTop: "1px solid #EFEBF7",
            }}
            onClick={() => setOpen(false)}
          >
            Send new invite
          </a>
          <button
            onClick={() => { setOpen(false); onArchive(); }}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              padding: "9px 14px",
              fontSize: 13,
              color: "#17131F",
              background: "none",
              border: "none",
              borderTop: "1px solid #EFEBF7",
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Archive
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function RecruiterCandidates() {
  const [data, setData] = useState<CandidatesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archivedRows, setArchivedRows] = useState<ArchivedCandidateRow[]>([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<number | string>>(new Set());

  const fetchCandidates = useCallback(() => {
    setLoading(true);
    setError(null);
    const path =
      activeFilter === "all"
        ? "/recruiter/candidates"
        : `/recruiter/candidates?status=${activeFilter}`;
    return api
      .get<CandidatesData>(path)
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message ?? "Failed to load candidates.");
        setLoading(false);
      });
  }, [activeFilter]);

  // Fetch candidates. Re-fetch when filter changes via ?status= param.
  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const fetchArchived = useCallback(() => {
    setArchivedLoading(true);
    api
      .get<{ rows: ArchivedCandidateRow[] }>("/recruiter/candidates/archived")
      .then((d) => setArchivedRows(d.rows))
      .finally(() => setArchivedLoading(false));
  }, []);

  function toggleArchivedSection() {
    setArchivedOpen((open) => {
      if (!open) fetchArchived();
      return !open;
    });
  }

  async function archiveOne(id: number) {
    if (!window.confirm("Archive this candidate? They will be hidden from the main list. You can restore them anytime.")) return;
    setBusyIds((prev) => new Set(prev).add(id));
    try {
      await api.post(`/recruiter/invite/${id}/archive`);
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
      await fetchCandidates();
    } catch (err) {
      window.alert(`Error: ${err instanceof Error ? err.message : "unknown"}`);
    } finally {
      setBusyIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }
  }

  async function unarchiveOne(id: string) {
    setBusyIds((prev) => new Set(prev).add(id));
    try {
      await api.post(`/recruiter/invite/${id}/unarchive`);
      setArchivedRows((prev) => prev.filter((r) => r.id !== id));
      await fetchCandidates();
    } catch (err) {
      window.alert(`Error: ${err instanceof Error ? err.message : "unknown"}`);
    } finally {
      setBusyIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }
  }

  async function bulkArchive() {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Archive ${ids.length} candidate(s)?`)) return;
    await Promise.all(ids.map((id) => api.post(`/recruiter/invite/${id}/archive`).catch(() => null)));
    setSelectedIds(new Set());
    await fetchCandidates();
  }

  async function bulkDeleteSessions() {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete session data for ${ids.length} candidate(s)? Transcripts, audio and scorecards are removed. This cannot be undone.`)) return;
    await Promise.all(ids.map((id) => api.post(`/recruiter/invite/${id}/delete-session`).catch(() => null)));
    setSelectedIds(new Set());
    await fetchCandidates();
  }

  // Client-side search filter on name / role / company
  const visibleRows = useMemo<CandidateRow[]>(() => {
    if (!data) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.role.toLowerCase().includes(q) ||
        r.company.toLowerCase().includes(q),
    );
  }, [data, searchQuery]);

  // Checkbox helpers
  const allVisibleIds = visibleRows.map((r) => r.id);
  const allChecked =
    allVisibleIds.length > 0 &&
    allVisibleIds.every((id) => selectedIds.has(id));
  const someChecked = !allChecked && allVisibleIds.some((id) => selectedIds.has(id));

  function toggleAll() {
    if (allChecked) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allVisibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allVisibleIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }

  function toggleRow(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Filter tabs definition
  const counts = data?.counts ?? {
    all: 0,
    needs_review: 0,
    invited: 0,
    completed: 0,
    expired: 0,
  };

  const TABS: Array<{ key: FilterKey; label: string; count: number }> = [
    { key: "all", label: "All", count: counts.all },
    { key: "needs_review", label: "Needs review", count: counts.needs_review },
    { key: "invited", label: "Invited", count: counts.invited },
    { key: "completed", label: "Completed", count: counts.completed },
    { key: "expired", label: "Expired", count: counts.expired },
  ];

  return (
    <RecruiterShell activeNav="candidates">
      {/* ── Page header ──────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: "#17131F",
              margin: 0,
              lineHeight: 1.25,
            }}
          >
            Candidates
          </h1>
          <p
            style={{
              fontSize: 14,
              color: "#7C7590",
              margin: "4px 0 0",
              fontWeight: 400,
            }}
          >
            Manage every invite, session and scorecard.
          </p>
        </div>

        <a
          href="/coach/recruiter/invite/new"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "0 20px",
            height: 40,
            borderRadius: 999,
            background: "var(--accent)",
            color: "#fff",
            fontSize: 13.5,
            fontWeight: 600,
            textDecoration: "none",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <Plus size={15} strokeWidth={2.5} />
          Send invite
        </a>
      </div>

      {/* ── Search bar ───────────────────────────────────────────────── */}
      <div style={{ position: "relative", marginBottom: 16 }}>
        <Search
          size={15}
          style={{
            position: "absolute",
            left: 13,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#8C86A0",
            pointerEvents: "none",
          }}
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search name, role or company..."
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "10px 14px 10px 36px",
            fontSize: 14,
            color: "#17131F",
            background: "#FFFFFF",
            border: "1px solid #EFEBF7",
            borderRadius: 10,
            outline: "none",
            fontFamily: "inherit",
          }}
        />
      </div>

      {/* ── Filter tabs ──────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        {TABS.map(({ key, label, count }) => {
          const isActive = activeFilter === key;
          return (
            <button
              key={key}
              onClick={() => setActiveFilter(key)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 13px",
                borderRadius: 999,
                border: isActive ? "1px solid #17131F" : "1px solid #EFEBF7",
                background: isActive ? "#17131F" : "transparent",
                color: isActive ? "#FBFAFE" : "#7C7590",
                fontSize: 12.5,
                fontWeight: 600,
                cursor: "pointer",
                letterSpacing: "0.01em",
                transition: "background 0.1s, color 0.1s, border-color 0.1s",
              }}
            >
              {label}
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 18,
                  height: 18,
                  padding: "0 4px",
                  borderRadius: 999,
                  background: isActive ? "rgba(255,255,255,0.18)" : "#EFEBF7",
                  color: isActive ? "#FBFAFE" : "#7C7590",
                  fontSize: 10.5,
                  fontWeight: 700,
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Bulk action bar ──────────────────────────────────────────── */}
      {selectedIds.size > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            background: "#17131F",
            color: "#FBFAFE",
            borderRadius: 10,
            padding: "10px 16px",
            marginBottom: 16,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedIds.size} selected</span>
          <div style={{ flex: 1 }} />
          <button
            onClick={bulkArchive}
            style={{ background: "rgba(255,255,255,.12)", color: "#FBFAFE", border: "none", borderRadius: 999, padding: "6px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
          >
            Archive
          </button>
          <button
            onClick={bulkDeleteSessions}
            style={{ background: "rgba(255,255,255,.12)", color: "#FBFAFE", border: "none", borderRadius: 999, padding: "6px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#E1573F"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,.12)"; }}
          >
            Delete sessions
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            style={{ background: "none", border: "none", color: "rgba(255,255,255,.7)", fontSize: 12.5, cursor: "pointer" }}
          >
            Clear
          </button>
        </div>
      )}

      {/* ── Loading / error states ───────────────────────────────────── */}
      {loading && (
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #EFEBF7",
            borderRadius: 18,
            padding: "48px 24px",
            textAlign: "center",
            color: "#8C86A0",
            fontSize: 14,
          }}
        >
          Loading candidates…
        </div>
      )}

      {!loading && error && (
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #EFEBF7",
            borderRadius: 18,
            padding: "32px 24px",
            textAlign: "center",
            color: "#E1573F",
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────────────── */}
      {!loading && !error && data && (
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #EFEBF7",
            borderRadius: 18,
            overflow: "hidden",
            boxShadow: "0 1px 2px rgba(45,30,90,.06)",
          }}
        >
          {visibleRows.length === 0 ? (
            /* ── Empty state ─────────────────────────────────────── */
            <div
              style={{
                padding: "64px 24px",
                textAlign: "center",
                color: "#7C7590",
                fontSize: 14,
              }}
            >
              {searchQuery
                ? `No candidates match "${searchQuery}".`
                : activeFilter === "all"
                ? "No candidates yet. Send your first invite to get started."
                : `No candidates with "${TABS.find((t) => t.key === activeFilter)?.label}" status.`}
            </div>
          ) : (
            <table
              className="rc-table"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                tableLayout: "fixed",
              }}
            >
              {/* ── Column widths ─────────────────────────────────── */}
              <colgroup>
                <col style={{ width: 44 }} />   {/* checkbox */}
                <col className="rc-col-candidate" />  {/* candidate */}
                <col style={{ width: 130 }} />   {/* status */}
                <col className="rc-col-score" style={{ width: 96 }} />    {/* score */}
                <col className="rc-col-activity" style={{ width: 120 }} />   {/* activity */}
                <col style={{ width: 44 }} />    {/* menu */}
              </colgroup>

              {/* ── Header ────────────────────────────────────────── */}
              <thead>
                <tr
                  style={{
                    borderBottom: "1px solid #EFEBF7",
                    background: "#FBFAFE",
                  }}
                >
                  <th style={{ padding: "10px 0 10px 14px" }}>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someChecked;
                      }}
                      onChange={toggleAll}
                      style={{ cursor: "pointer", accentColor: "var(--accent)" }}
                      aria-label="Select all"
                    />
                  </th>
                  {(
                    [
                      ["CANDIDATE", "left"],
                      ["STATUS", "left"],
                      ["SCORE", "left"],
                      ["ACTIVITY", "left"],
                    ] as const
                  ).map(([label, align]) => (
                    <th
                      key={label}
                      className={
                        label === "SCORE"
                          ? "rc-col-score"
                          : label === "ACTIVITY"
                            ? "rc-col-activity"
                            : undefined
                      }
                      style={{
                        padding: "10px 12px",
                        textAlign: align,
                        fontFamily: "var(--font-mono, ui-monospace, monospace)",
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.14em",
                        color: "#7C7590",
                      }}
                    >
                      {label}
                    </th>
                  ))}
                  {/* menu column — no header text */}
                  <th />
                </tr>
              </thead>

              <tbody>
                {/* ── Group header row ──────────────────────────── */}
                <tr style={{ background: "#F3F1FA" }}>
                  <td />
                  <td
                    colSpan={5}
                    style={{
                      padding: "7px 12px",
                      fontFamily: "var(--font-mono, ui-monospace, monospace)",
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.14em",
                      color: "#7C7590",
                    }}
                  >
                    ALL OTHER CANDIDATES
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: 8,
                        minWidth: 18,
                        height: 18,
                        padding: "0 5px",
                        borderRadius: 999,
                        background: "#EFEBF7",
                        color: "#7C7590",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 0,
                      }}
                    >
                      {visibleRows.length}
                    </span>
                  </td>
                </tr>

                {/* ── Candidate rows ────────────────────────────── */}
                {visibleRows.map((row, idx) => {
                  const isSelected = selectedIds.has(row.id);
                  const isLast = idx === visibleRows.length - 1;
                  return (
                    <tr
                      key={row.id}
                      onClick={() =>
                        (window.location.href = `/coach/recruiter/candidate/${encodeURIComponent(row.email)}`)
                      }
                      style={{
                        borderBottom: isLast ? "none" : "1px solid #EFEBF7",
                        background: isSelected ? "#EAF5F5" : "#FFFFFF",
                        cursor: "pointer",
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected)
                          (e.currentTarget as HTMLTableRowElement).style.background =
                            "#F7F3EC";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background =
                          isSelected ? "#EAF5F5" : "#FFFFFF";
                      }}
                    >
                      {/* Checkbox */}
                      <td
                        style={{ padding: "12px 0 12px 14px" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRow(row.id)}
                          style={{ cursor: "pointer", accentColor: "var(--accent)" }}
                          aria-label={`Select ${row.name}`}
                        />
                      </td>

                      {/* Candidate name + role · company */}
                      <td style={{ padding: "12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <Avatar name={row.name} />
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: "#17131F",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {row.name}
                            </div>
                            <div
                              style={{
                                fontSize: 12.5,
                                color: "#7C7590",
                                marginTop: 1,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {row.role}
                              {row.company ? ` · ${row.company}` : ""}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Status badge */}
                      <td style={{ padding: "12px" }}>
                        <StatusBadge status={row.status} />
                      </td>

                      {/* Score */}
                      <td className="rc-col-score" style={{ padding: "12px" }}>
                        {row.score ? (
                          <span
                            style={{
                              fontSize: 13.5,
                              fontWeight: 600,
                              color: row.score_color || "#17131F",
                              whiteSpace: "nowrap",
                              fontFamily:
                                "var(--font-mono, ui-monospace, monospace)",
                            }}
                          >
                            {row.score}
                          </span>
                        ) : (
                          <span style={{ fontSize: 13, color: "#9A93AD" }}>—</span>
                        )}
                      </td>

                      {/* Activity date */}
                      <td className="rc-col-activity" style={{ padding: "12px" }}>
                        <span style={{ fontSize: 12.5, color: "#7C7590" }}>
                          {formatActivity(row.last_activity)}
                        </span>
                      </td>

                      {/* Row menu */}
                      <td
                        style={{ padding: "12px 10px 12px 0", textAlign: "right" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <RowMenu email={row.email} onArchive={() => archiveOne(row.id)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Archived candidates ──────────────────────────────────────── */}
      {!loading && !error && (
        <div style={{ marginTop: 20 }}>
          <button
            onClick={toggleArchivedSection}
            style={{
              background: "none",
              border: "none",
              fontFamily: "var(--font-mono, ui-monospace, monospace)",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#7C7590",
              cursor: "pointer",
              padding: 0,
            }}
          >
            {archivedOpen ? "▾" : "▸"} Archived
          </button>

          {archivedOpen && (
            <div
              style={{
                marginTop: 10,
                background: "#FFFFFF",
                border: "1px solid #EFEBF7",
                borderRadius: 18,
                overflow: "hidden",
              }}
            >
              {archivedLoading ? (
                <div style={{ padding: "24px", textAlign: "center", color: "#8C86A0", fontSize: 13.5 }}>
                  Loading…
                </div>
              ) : archivedRows.length === 0 ? (
                <div style={{ padding: "24px", textAlign: "center", color: "#7C7590", fontSize: 13.5 }}>
                  No archived candidates.
                </div>
              ) : (
                archivedRows.map((row, idx) => (
                  <div
                    key={row.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px 16px",
                      borderBottom: idx === archivedRows.length - 1 ? "none" : "1px solid #EFEBF7",
                    }}
                  >
                    <Avatar name={row.name} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#17131F" }}>{row.name}</div>
                      <div style={{ fontSize: 12.5, color: "#7C7590" }}>
                        {row.role}{row.company ? ` · ${row.company}` : ""} · Archived {formatActivity(row.archived_at)}
                      </div>
                    </div>
                    <button
                      onClick={() => unarchiveOne(row.id)}
                      disabled={busyIds.has(row.id)}
                      style={{
                        padding: "5px 14px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#17131F",
                        background: "transparent",
                        border: "1px solid #E0DBD3",
                        borderRadius: 999,
                        cursor: busyIds.has(row.id) ? "default" : "pointer",
                        opacity: busyIds.has(row.id) ? 0.6 : 1,
                      }}
                    >
                      Restore
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Responsive overrides ─────────────────────────────────────── */}
      <style>{`
        @media (max-width: 720px) {
          /* fixed layout still counts a hidden col's declared width toward the
             row total, so zero it out too or the candidate column shrinks
             and STATUS overlaps it. */
          col.rc-col-score, col.rc-col-activity { width: 0 !important; }
          .rc-col-score, .rc-col-activity { display: none !important; }
          /* with score/activity zeroed, give the flexible candidate col the
             leftover space explicitly — fixed layout doesn't do this on its own. */
          col.rc-col-candidate { width: 160px !important; }
        }
      `}</style>
    </RecruiterShell>
  );
}
